// A BLE lamp group's ROOM CARD on the desktop — a compact copy of the PHONE
// screen (ui/shared/ble/BleNodePanel), in the desktop card style of the Wi-Fi
// light card (LightControlCard):
//
//   Group       the two sliders (Brightness, Warm ↔ Cool), the four presets
//               (Warm White · Cool White · White · Off), All Off / All On, Blink
//   Individual  the PHONE'S lamp list itself (BleNodePanel view="individual"):
//               a row per lamp, its sliders, blink, ON/OFF, ⋮ menu, Settings
//
// Every press is an MQTT command to the location's hub (ESP32BLEGATEWAY,
// docs/20) — the desktop has no Bluetooth and needs none. Same hook as the
// phone (useBleNode), so both shells agree. The ⧉ icon opens the full page.
//
// Without a hub, or for a LampSmart group, it falls back to the "control from
// the phone" notice the caller passes in.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, ButtonBase, Card, CircularProgress, IconButton, Slider, Tab, Tabs, Tooltip, Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import { AllButtons } from '../controls/AllBar';
import BleNodePanel from './BleNodePanel';
import { MessageDialogProvider } from '../MessageDialog';
import BleGroupMenu from './BleGroupMenu';
import OrphanBleRemove from './OrphanBleRemove';
import { useApp } from '../../../core/state/AppContext';
import { useBleNode } from '../../../core/hooks/useBleNode';
import { countOn, lastUsedText } from '../../../core/ble/lampState';
import { MESH_PRESETS, parseMeshValues } from '../../../core/ble/mesh/meshValues';
import { CARD_TYPE } from '../../../core/devices/buttonLayout';
import { offlineSinceText } from '../../../core/ble/mesh/hubStatus';
const SLIDERS = [
  { label: 'Brightness', color: '#e0e0e0' },
  { label: 'Warm ↔ Cool', color: '#90caf9' },
];

function Presets({ onPreset, disabled }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'space-between' }}>
      {MESH_PRESETS.map((p) => (
        <Tooltip key={p.label} title={p.label.replace('\n', ' ')}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <ButtonBase
              disabled={disabled}
              onClick={() => onPreset(p.command)}
              sx={{
                width: '100%', flexDirection: 'column', gap: 0.25, py: 0.5, borderRadius: 2, border: 1, borderColor: 'divider',
              }}
            >
              <Box sx={{
                width: 22, height: 22, borderRadius: '6px', bgcolor: p.swatch, border: '1px solid rgba(0,0,0,0.2)',
              }}
              />
              <Typography noWrap sx={{ fontSize: 10, fontWeight: 600, lineHeight: 1.1, maxWidth: '100%' }}>
                {p.label.replace('\n', ' ')}
              </Typography>
            </ButtonBase>
          </span>
        </Tooltip>
      ))}
    </Box>
  );
}

/**
 * The ⋮ (Rename / Delete group) is on EVERY form of the card — the hub card,
 * the empty group, and the phone-only notice (`fallback(device, menu)`) — so a
 * group can always be renamed or, when empty, deleted from the room screen.
 * A rename shows at once; a deleted group leaves the grid at once.
 */
export default function BleNodeCard({ device, subtitle, fallback }) {
  const [name, setName] = useState(null);
  const [gone, setGone] = useState(false);
  if (gone) return null;
  const dev = name ? { ...device, DeviceName: name } : device;
  const menu = <BleGroupMenu device={dev} name={name} onRenamed={setName} onDeleted={() => setGone(true)} />;
  return (
    <BleNodeCardBody
      device={dev}
      subtitle={subtitle}
      menu={menu}
      onGone={() => setGone(true)}
      fallback={typeof fallback === 'function' ? fallback(dev, menu) : fallback}
    />
  );
}

function BleNodeCardBody({
  device, subtitle, fallback, menu, onGone,
}) {
  const { userId } = useApp();
  const navigate = useNavigate();
  const ctl = useBleNode(device?.UniqueNumber, userId);
  const { node, lamps } = ctl;
  const [tab, setTab] = useState('group');
  const [values, setValues] = useState([0, 128]);
  useEffect(() => { if (node) setValues(ctl.nodeValues); }, [node?.LastValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const shell = (children) => (
    <Card sx={{
      p: 1.25, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.75, bgcolor: 'background.paper', border: 1, borderColor: 'divider',
    }}
    >
      {children}
    </Card>
  );

  // Spin only while the group itself loads. A failed load SAYS so (it used to
  // spin forever, waiting for a hub lookup that never started).
  if (ctl.loading) {
    return shell(<Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={22} /></Box>);
  }
  if (!node) {
    return shell(
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={700} color="text.primary" noWrap sx={{ fontSize: CARD_TYPE.title }}>
            {device.DeviceName || device.UniqueNumber}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 12 }}>
            {ctl.error || 'Could not load this device'}
          </Typography>
          {ctl.orphan && (
            <Box sx={{ mt: 1 }}><OrphanBleRemove device={device} onRemoved={onGone} /></Box>
          )}
        </Box>
      </Box>,
    );
  }
  if (!ctl.isMesh || ctl.hubUno === null) return fallback;
  const finding = ctl.hubUno === undefined; // drawn; controls wait for the hub
  if (lamps.length === 0) {
    return shell(
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography fontWeight={700} color="text.primary" noWrap sx={{ fontSize: CARD_TYPE.title, flex: 1, minWidth: 0 }}>
            {device.DeviceName || device.UniqueNumber}
          </Typography>
          {menu}
        </Box>
        <Typography color="text.secondary" sx={{ fontSize: 12 }}>
          No lamps in this group yet — add them from the phone app, near the lamps.
        </Typography>
      </>,
    );
  }
  // The desktop has no radio: with the hub down (or without this location's
  // keys) nothing on this card can reach a lamp, so it is drawn GREYED and says
  // since when — never live-looking controls that do nothing.
  const hubDown = ctl.hubLive === false;
  const noKeys = ctl.hubLive === true && ctl.hubReady === false;
  const blocked = hubDown || noKeys;
  const off = finding || blocked || ctl.busy;
  const hubLine = hubDown
    ? `Hub offline${ctl.hubLastLiveAt ? ` ${offlineSinceText(ctl.hubLastLiveAt)}` : ''}`
    : noKeys ? 'Hub has no mesh keys for this location' : null; // eslint-disable-line no-nested-ternary

  const k = countOn(lamps, { mesh: true });
  const used = lastUsedText(node);
  const open = () => navigate(`/device/${device.UniqueNumber}`, { state: { locationId: node.LocationID, roomId: node.RoomID } });
  // Every button moves the sliders: success via the new LastValues (Off =
  // brightness 0), failure back to what the lamps last had.
  const revertIfFailed = (ok) => { if (ok === false) setValues(ctl.nodeValues); };
  const preset = (cmd) => {
    const p = parseMeshValues(cmd);
    if (!p) return;
    const v = p[0] === 0 ? [0, values[1] || 0] : p; // Off keeps the colour
    setValues(v);
    ctl.setValues(v[0], v[1]).then(revertIfFailed);
  };

  return shell(
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1, cursor: 'pointer' }} onClick={open}>
          {subtitle && (
            <Typography color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2, fontSize: CARD_TYPE.subtitle }}>
              {subtitle}
            </Typography>
          )}
          <Typography fontWeight={700} color="text.primary" noWrap sx={{ fontSize: CARD_TYPE.title }}>
            {device.DeviceName || device.UniqueNumber}
          </Typography>
          <Typography color="text.secondary" noWrap sx={{ fontSize: CARD_TYPE.subtitle }}>
            {`${k.on} of ${k.total} on${used ? ` · ${used}` : ''}${blocked ? '' : ' · via hub'}`}
          </Typography>
          {hubLine && (
            <Typography noWrap sx={{ color: '#ef5350', fontSize: CARD_TYPE.subtitle, fontWeight: 700 }}>
              {hubLine}
            </Typography>
          )}
        </Box>
        <Tooltip title="Open full screen">
          <IconButton size="small" onClick={open}><OpenInNewIcon fontSize="small" /></IconButton>
        </Tooltip>
        {menu}
      </Box>

      <Box sx={{
        display: 'flex', flexDirection: 'column', gap: 0.75,
        opacity: blocked ? 0.45 : 1, pointerEvents: blocked ? 'none' : 'auto', filter: blocked ? 'grayscale(1)' : 'none',
      }}
      >
      {lamps.length >= 2 && (
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{ minHeight: 30, '& .MuiTab-root': { minHeight: 30, py: 0.25, fontSize: 12, fontWeight: 700 } }}
        >
          <Tab value="group" label={`Group (${lamps.length})`} />
          <Tab value="individual" label="Individual" />
        </Tabs>
      )}

      {(tab === 'group' || lamps.length < 2) && (
        <Box sx={{ opacity: finding ? 0.5 : 1, pointerEvents: finding ? 'none' : 'auto', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-around', alignItems: 'flex-end', pt: 0.5 }}>
            {SLIDERS.map((s, i) => (
              <Box key={s.label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                <Slider
                  orientation="vertical"
                  min={0}
                  max={255}
                  value={values[i] || 0}
                  disabled={off}
                  onChange={(_, v) => setValues((prev) => { const u = [...prev]; u[i] = v; return u; })}
                  onChangeCommitted={(_, v) => { const u = [...values]; u[i] = v; ctl.setValues(u[0], u[1]).then(revertIfFailed); }}
                  sx={{
                    height: 88, color: s.color, '& .MuiSlider-rail': { opacity: 0.25 }, '& .MuiSlider-thumb': { width: 13, height: 13 },
                  }}
                />
                <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ lineHeight: 1 }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>
          <Presets onPreset={preset} disabled={off} />
          <AllButtons onAll={(on) => (on ? ctl.turnOn() : ctl.turnOff())} />
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Button size="small" variant="outlined" disabled={off} onClick={() => ctl.nightMode()} sx={{ flex: 1, color: 'text.primary', borderColor: 'divider' }}>Night</Button>
            <Button size="small" variant="outlined" disabled={off} startIcon={<FlashOnIcon />} onClick={() => ctl.blink()} sx={{ flex: 1, color: 'text.primary', borderColor: 'divider' }}>Blink</Button>
          </Box>
        </Box>
      )}

      {/* Individual = the PHONE'S list, the same component: one row per lamp
          (however many), ▾ for that lamp's sliders and presets, ⚡ blink, the
          ON/OFF switch, ⋮ rename / room / reverse / lock / remove — and the
          Lamps header with Lock, ⟳ (read the real states) and Settings. */}
      {tab === 'individual' && lamps.length >= 2 && (
        <Box sx={{ mx: -1.25, mb: -1.25, borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
          <MessageDialogProvider>
            <BleNodePanel device={device} remoteOnly view="individual" />
          </MessageDialogProvider>
        </Box>
      )}
      </Box>

      {finding && <Typography color="text.secondary" sx={{ fontSize: 11 }}>Finding this location’s hub…</Typography>}
      {ctl.busy && <Typography color="text.secondary" sx={{ fontSize: 11 }}>Sending through the hub…</Typography>}
      {ctl.failed && !!ctl.status && <Typography sx={{ color: '#ef5350', fontSize: 11 }}>{ctl.status}</Typography>}
    </>,
  );
}
